// src/utils/compressImage.js
// Compress images before uploading to Supabase
// Runs entirely in the browser — no server needed, no cost
// Converts to JPEG, caps dimensions, reduces file size by 60-80%

const MAX_DIMENSIONS = {
  cover:      { w: 1440, h: 600  },  // hero banner
  logo:       { w: 400,  h: 400  },  // logo square
  logo_dark:  { w: 400,  h: 400  },  // dark logo
  founder:    { w: 600,  h: 800  },  // portrait
  about_main: { w: 900,  h: 600  },  // landscape
  about_sm:   { w: 600,  h: 800  },  // side portrait
  gallery:    { w: 900,  h: 900  },  // gallery (max dim)
  product:    { w: 800,  h: 1067 },  // product portrait
  default:    { w: 1200, h: 1200 },  // fallback
}

const QUALITY = 0.85  // 85% quality — visually identical, ~60% smaller than original

/**
 * Compress an image file before upload
 * @param {File} file - The original image file
 * @param {string} type - Upload type: 'cover', 'logo', 'gallery', 'product', etc.
 * @returns {Promise<File>} - Compressed JPEG file
 */
export async function compressImage(file, type = 'default') {
  return new Promise((resolve, reject) => {
    // Skip compression for tiny files (< 100KB) — already small enough
    if (file.size < 100 * 1024) {
      resolve(file)
      return
    }

    const dims = MAX_DIMENSIONS[type] || MAX_DIMENSIONS.default
    const reader = new FileReader()

    reader.onload = (e) => {
      const img = new Image()

      img.onload = () => {
        // Calculate target dimensions maintaining aspect ratio
        let { w: targetW, h: targetH } = dims
        const aspectRatio = img.width / img.height

        if (img.width > targetW || img.height > targetH) {
          if (img.width / targetW > img.height / targetH) {
            // Width is the constraining dimension
            targetH = Math.round(targetW / aspectRatio)
          } else {
            // Height is the constraining dimension
            targetW = Math.round(targetH * aspectRatio)
          }
        } else {
          // Image is already smaller than max — keep original dimensions
          targetW = img.width
          targetH = img.height
        }

        // Draw to canvas at target dimensions
        const canvas = document.createElement('canvas')
        canvas.width  = targetW
        canvas.height = targetH
        const ctx = canvas.getContext('2d')

        // Smooth scaling
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        ctx.drawImage(img, 0, 0, targetW, targetH)

        // Convert to JPEG blob
        canvas.toBlob(
          (blob) => {
            if (!blob) { resolve(file); return }

            // Only use compressed version if it's actually smaller
            if (blob.size >= file.size) {
              resolve(file)
              return
            }

            const compressed = new File(
              [blob],
              file.name.replace(/\.[^.]+$/, '.jpg'),
              { type: 'image/jpeg', lastModified: Date.now() }
            )

            const savings = Math.round((1 - blob.size / file.size) * 100)
            console.log(`[compress] ${file.name}: ${(file.size/1024).toFixed(0)}KB → ${(blob.size/1024).toFixed(0)}KB (${savings}% smaller, ${targetW}×${targetH}px)`)

            resolve(compressed)
          },
          'image/jpeg',
          QUALITY
        )
      }

      img.onerror = () => resolve(file) // On error, use original
      img.src = e.target.result
    }

    reader.onerror = () => resolve(file)
    reader.readAsDataURL(file)
  })
}

/**
 * Get human-readable file size
 */
export function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB'
  return (bytes / 1024 / 1024).toFixed(1) + ' MB'
}